import useRouter from 'common/hooks/use-router';
import useItem from 'common/hooks/useItem';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import AlertsContext from 'common/providers/alerts';
import MediatorForm from './MediatorForm';
import useAuth from 'common/hooks/useAuth';
import useList from 'common/hooks/useList';

function Mediator() {
  const { t } = useTranslation();
  const router = useRouter();
  const { setAlert } = useContext(AlertsContext);
  const { user: loggedInUser } = useAuth();

  const id = router.query.id as string;
  const userId = router.query.userId as string;

  const {
    updateItem: updateUser,
    item: user,
    error: userError,
    isFetching,
  }: any = useItem('users', id || userId, {
    populate: ['operator.establishments', 'operator.establishments.department', 'role'],
  });

  const { fetchItems: refreshUsers, error: usersError } = useList('users', {
    populate: ['operator.establishments', 'operator.establishments.department', 'role'],
  });

  const {
    saveItem: saveOperator,
    updateItem: updateOperator,
    error: operatorError,
  }: any = useItem('operators', null, {
    manual: true,
  });

  const isMyAccount = `${loggedInUser?.id}` === id || `${loggedInUser?.id}` === userId;

  useEffect(() => {
    if (userError || usersError || operatorError) {
      const e = userError || usersError || operatorError;
      setAlert(
        e === 'This attribute must be unique' ? t('common.emailAlreadyTaken') : t('common.error'),
        'danger'
      );
    }
  }, [userError, usersError, operatorError, setAlert, t]);

  const back = useCallback(() => {
    if (!userError && !usersError && !operatorError) {
      setAlert(t('common.saved'), 'success');
      if (isMyAccount) {
        router.navigate('/my-account-edit');
      } else {
        router.navigate('/mediators');
      }
    }
  }, [setAlert, router, userError, usersError, operatorError, t]);

  const onSubmit = useCallback(
    async (dataOperator: any) => {
      const operator = { ...dataOperator };

      try {
        if (operator.id) {
          // mise en parallèle des mises à jour user et profil
          const updatePromises = [];

          // Ajouter la mise à jour du profil
          updatePromises.push(updateOperator(operator.id, operator));

          // Vérifier si les données utilisateur modifiables ont changé
          if (
            operator.user.email !== user.email ||
            operator.user.blocked !== user.blocked ||
            operator.user.confirmed !== user.confirmed
          ) {
            // Ajouter la mise à jour de l'utilisateur
            updatePromises.push(
              updateUser(id, {
                ...user,
                email: operator.user.email,
                blocked: operator.user.blocked,
                confirmed: operator.user.confirmed,
              })
            );
          }

          // Exécuter les requêtes simultanément
          await Promise.all(updatePromises);
        } else {
          await saveOperator({ ...operator, user: userId });
        }
        // refresh item
        await refreshUsers();
      } catch (e: any) {
        return e;
      }

      back();
      return true;
    },
    [
      updateOperator,
      back,
      saveOperator,
      userId,
      loggedInUser,
      user,
      id,
      userError,
      usersError,
      operatorError,
      t,
    ]
  );

  return isFetching || !user ? null : (
    <MediatorForm onSubmit={onSubmit} editedUser={user} myAccount={isMyAccount} />
  );
}

export default Mediator;
